import { useState } from 'react';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch, HiOutlineViewGrid, HiOutlineViewList, HiOutlineLocationMarker } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { Modal, Alert } from '../../../commons';
import { useCountries, useCountryTours, useSearch } from '../../../../hooks';
import TourCard from './TourCard';
import { DAV_APIS } from '../../../../adapters';
import DashboardLoader from '../../sections/Loader/DashboardLoader';
import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';

const Tours = () => {
  const [selectedCountry, setSelectedCountry] = useState('uganda');
  const [selectedTour, setSelectedTour] = useState('' as string | null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [refresh, setRefresh] = useState<number | null>(null);
  const { countries, loading: countryLoading } = useCountries();
  const { tours, loading } = useCountryTours(selectedCountry, refresh);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('' as string);

  const navigate = useNavigate();

  const countriesOptions = countries.map((country) => ({
    value: country.name,
    label: country.name,
  }));

  const handleDeleteTour = async () => {
    setIsLoading(true);
    try {
      const res = await DAV_APIS.tour.deleteTourById(selectedTour as string);
      if (res.status === 204) {
        Alert({ message: 'Deleted Successfully', variant: 'success' });
      }
      setIsLoading(false);
      setShowModal(false);
      setRefresh(Math.random());
    } catch (error) {
      setIsLoading(false);
      setShowModal(false);
      Alert({ message: 'Failed to delete tour', variant: 'error' });
    }
  };

  const toursResults = useSearch(tours, searchTerm);

  const renderContent = () => {
    if (loading) {
      return (
        <div className='flex flex-col items-center justify-center py-20 animate-pulse'>
          <DashboardLoader />
        </div>
      );
    }

    if (toursResults.length > 0) {
      return (
        <div className={`grid gap-6 ${layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
          {toursResults.map((tour, index) => (
            <div key={tour.id} style={{ animationDelay: `${index * 50}ms` }} className='animate-in fade-in slide-in-from-bottom-2 duration-500'>
              <TourCard
                tour={tour}
                layout={layout}
                onEditHandler={() => navigate(`/dashboard/tours/edit?tour=${tour.id}`)}
                onDeleteHandler={() => {
                  setShowModal(true);
                  setSelectedTour(tour.id);
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <Card variant='glass' className='py-20 flex flex-col items-center justify-center text-center'>
        <div className='w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4'>
          <HiOutlineSearch className='w-10 h-10 text-gray-400' />
        </div>
        <h3 className='text-lg font-bold text-gray-900 dark:text-white'>No packages found</h3>
        <p className='text-gray-500 dark:text-gray-400 max-w-sm mt-1 px-6'>
          {searchTerm ? 
            `We couldn't find any tours matching "${searchTerm}" in ${selectedCountry}.`
            : `There are currently no active tours for ${selectedCountry}.`}
        </p>
        {searchTerm && (
          <button 
            type='button'
            onClick={() => setSearchTerm('')}
            className='mt-4 text-primary-600 font-bold hover:underline'
          >
            Clear search
          </button>
        )}
      </Card>
    );
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Page Header */}
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Safari Packages
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Manage and curate your safari experiences for {selectedCountry}.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300'>
             <HiOutlineLocationMarker className='w-4 h-4' />
             {tours.length} Tours
          </div>
          <Button
            variant='primary'
            onClick={() => navigate('/dashboard/tours/new')}
          >
            <div className='flex items-center gap-2'>
              <HiOutlinePlus className='w-5 h-5' />
              <span>Create Tour</span>
            </div>
          </Button>
        </div>
      </div>

      {/* Controls Bar */}
      <Card variant='glass' className='p-4 border-none shadow-lg shadow-gray-200/40 dark:shadow-none'>
        <div className='flex flex-wrap items-center gap-4'>
          {/* Search */}
          <div className='relative flex-1 min-w-[240px]'>
            <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'>
              <HiOutlineSearch className='w-5 h-5' />
            </div>
            <input
              type='text'
              placeholder='Search packages by name or slug...'
              onChange={(e) => setSearchTerm(e.target.value)}
              className='
                w-full pl-11 pr-4 py-2.5 bg-white/50 dark:bg-gray-800/50 
                border border-gray-200 dark:border-gray-700 rounded-xl
                focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                placeholder:text-gray-400 dark:placeholder:text-gray-500
                transition-all duration-200
              '
            />
          </div>

          <div className='flex items-center gap-3 ml-auto'>
            {/* Country Selector */}
            <div className='relative min-w-[160px]'>
              <select
                onChange={(e) => setSelectedCountry(e.target.value)}
                value={selectedCountry}
                className='
                  w-full px-4 py-2.5 bg-white/50 dark:bg-gray-800/50 
                  border border-gray-200 dark:border-gray-700 rounded-xl
                  appearance-none cursor-pointer focus:ring-2 focus:ring-primary-500/20
                  text-gray-700 dark:text-gray-300 font-medium
                  transition-all duration-200 capitalize
                '
              >
                {countryLoading ? (
                  <option value=''>Loading Countries...</option>
                ) : (
                  countriesOptions.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))
                )}
              </select>
              <div className='absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400'>
                <svg className='w-4 h-4 fill-current' viewBox='0 0 20 20'>
                  <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                </svg>
              </div>
            </div>

            {/* Layout Toggle */}
            <div className='flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700/50'>
              <button
                type='button'
                onClick={() => setLayout('grid')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'grid' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='Grid View'
              >
                <HiOutlineViewGrid className='w-5 h-5' />
              </button>
              <button
                type='button'
                onClick={() => setLayout('list')}
                className={`
                  p-1.5 rounded-md transition-all duration-200
                  ${layout === 'list' ?
                    'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
                title='List View'
              >
                <HiOutlineViewList className='w-5 h-5' />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              type='button'
              onClick={() => setRefresh(Math.random())}
              disabled={loading}
              className={`
                p-2.5 rounded-xl border border-gray-200 dark:border-gray-700
                bg-white/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400
                hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400
                transition-all duration-200 shadow-sm
                ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}
              `}
              title='Refresh list'
            >
              <HiOutlineRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </Card>

      {/* Content Grid */}
      <div className='min-h-[400px]'>
        {renderContent()}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        size='sm'
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleDeleteTour}
        loading={isLoading}
        successText='Delete Tour'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Confirm Deletion</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
            Are you sure you want to delete this tour? This action will remove the package permanently and cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Tours;
